import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

async function ExportToExcel({
    data,
    branches,
    branchId,
    filter,
    filterDateStart,
    filterDateEnd,
    snType,
    t,
}: {
    data: any
    branches: any[]
    branchId: number
    filter: any[]
    filterDateStart: string
    filterDateEnd: string
    snType: boolean
    t: any
}) {
    try {
        if (!Array.isArray(data.rows) || data.rows.length === 0) {
            alert(t('REPORT.TITLE.NO_DATA'));
            return;
        }
        const workbook = new ExcelJS.Workbook()
        const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.NEW_PATIENT'))

        const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.NEW_PATIENT')])
        titleRow.font = { bold: true, size: 16 }
        sheet.mergeCells(titleRow.number, 1, titleRow.number, 20)
        titleRow.alignment = { horizontal: 'left' }
        // Filter info (horizontal)
        const filterLabels = [
            t('REPORT.FILTER.BRANCH'),
            t('REPORT.FILTER.DATE_START'),
            t('REPORT.FILTER.DATE_END'),
            t('REPORT.FILTER.SERVICE_ACCESS'),
        ]
        const filterValues = [
            branchId === 0 ? t('REPORT.FILTER.ALL') : _.get(_.find(branches, { branchId }), 'branchName', ''),
            filterDateStart ? toBuddhistYear(moment(filterDateStart), 'DD/MM/YYYY') : '-',
            filterDateEnd ? toBuddhistYear(moment(filterDateEnd), 'DD/MM/YYYY') : '-',
            filter[2] || t('REPORT.FILTER.ALL'),
        ]

        const filterRowData: any[] = []
        for (let i = 0; i < filterLabels.length; i++) {
            filterRowData.push(filterLabels[i] + ':', filterValues[i])
        }
        const filterRow = sheet.addRow(filterRowData)
        filterRow.font = { size: 11 }
        for (let col = 1; col <= filterRowData.length; col += 2) {
            filterRow.getCell(col).font = { bold: true }
            filterRow.getCell(col).alignment = { horizontal: 'left' }
            filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
        }
        sheet.addRow([])

        const headCells = [
            { label: t('REPORT.TABLE.NEW_PATIENT.#NO'), width: 10 },
            { label: t('REPORT.TABLE.NEW_PATIENT.DATE'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.TIME'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.BRANCH'), width: 20 },
            { label: t('REPORT.TABLE.NEW_PATIENT.VN_NUMBER'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.PATIENT_CN_NUMBER'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.PATIENT_SN_CODE'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.PATIENT_FULLNAME'), width: 25 },
            { label: t('REPORT.TABLE.NEW_PATIENT.GENDER'), width: 10 },
            { label: t('REPORT.TABLE.NEW_PATIENT.PHONE'), width: 15 },
            { label: t('REPORT.TABLE.NEW_PATIENT.ORIGINNAME'), width: 20 },
            { label: t('REPORT.TABLE.NEW_PATIENT.ACCESS_CHANAL'), width: 20 },
            { label: t('REPORT.TABLE.NEW_PATIENT.FIRST_BILL'), width: 20 },
            { label: t('REPORT.TABLE.NEW_PATIENT.PAY_FIRST_BILL'), width: 15 },
        ]
        if (!snType) {
            headCells.splice(6, 1)
        }

        sheet.columns = headCells.map(c => ({ width: c.width }))

        const headerRow = sheet.addRow(headCells.map(c => c.label))
        headerRow.font = { bold: true, size: 12 }
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

        for (const row of data.rows) {
            const rowData = [
                data.rows.indexOf(row) + 1,
                row.date || '-',
                row.time || '-',
                row.branchName || '-',
                row.cnNumber || '-',
                row.branchCnNumber || '-',
                row.snCode || '-',
                row.fullName || '-',
                row.gender || '-',
                row.phone || '-',
                row.originName || '-',
                row.channelName || '-',
                row.operativeName || '-',
                row.totalPrice || '-',
            ]
            if (!snType) {
                rowData.splice(6, 1)
            }
            sheet.addRow(rowData)
        }

        // Summary table
        sheet.addRow([])
        sheet.addRow([])

        const summaryTitleRow = sheet.addRow([t('')])
        summaryTitleRow.font = { bold: true, size: 14 }
        sheet.mergeCells(summaryTitleRow.number, 1, summaryTitleRow.number, 20)
        summaryTitleRow.alignment = { horizontal: 'center' }

        const summaryHeaderRow = sheet.addRow([
            t('REPORT.TABLE.NEW_PATIENT.ACCESS_CHANAL'),
            t('REPORT.TABLE.NEW_PATIENT.NEW_PATIENT_TOTAL')
        ])
        summaryHeaderRow.font = { bold: true, size: 12 }
        summaryHeaderRow.alignment = { horizontal: 'center' }

        // Group by access channel and count
        const channelGroups = _.groupBy(data.rows, 'channelName')
        const summaryData = Object.entries(channelGroups).map(([channelName, patients]) => [
            channelName || t('REPORT.FILTER.UNKNOWN'),
            patients.length
        ])

        // Add total row
        summaryData.push([
            t('REPORT.TABLE.NEW_PATIENT.TOTAL'),
            data.rows.length
        ])

        summaryData.forEach(row => {
            const summaryRow = sheet.addRow(row)
            summaryRow.alignment = { horizontal: 'center' }
            if (row[0] === t('REPORT.TABLE.NEW_PATIENT.TOTAL')) {
                summaryRow.font = { bold: true }
            }
        })

        forceTextOnDateCells(workbook)
        const buffer = await workbook.xlsx.writeBuffer()
        const timestamp = moment().format('YYYYMMDD_HHmmss')
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `${t('REPORT.REPORT_NAME.NEW_PATIENT')}_${timestamp}.xlsx`)
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
}

export default ExportToExcel 